import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  SaasCancelSubscriptionFailureToastMessage,
  SaasCancelSubscriptionSuccessToastMessage,
  SaasEnterpriseRequestFailureToastMessage,
  SaasEnterpriseRequestSuccessToastMessage,
} from 'src/app/Toast/Toast'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ChurnSurveyReasonsResponse,
  ChurnSurveyRequest,
  OrganizationSaasTierResponse,
  OrganizationUsagesResponse,
  SaasTierType,
  SaasUrlResponse,
  SubscriptionResponse,
} from 'src/models/organizations.models'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  RequestConfig,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'

interface OrganizationSaasTierOptions
  extends ExtendBaseQueryOptions<OrganizationSaasTierResponse> {
  orgId: string | undefined
}

export const useQueryOrganizationSaasTier = ({
  orgId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: OrganizationSaasTierOptions) => {
  return useExtendQuery<OrganizationSaasTierResponse>({
    queryKey: orgQueryKeys.saasTier(orgId),
    queryUrl: () => `saas/${orgId}/tier`,
    // wait for orgId to be defined before making the request
    enabled: enabled && !!orgId,
    ...queryCacheOptions,
    ...rest,
  })
}

/**
 * @returns whether an org has access to saas features
 */
export const useHasSaasFeatures = (): boolean => {
  // A user has SaaS features if they are excluded from SaaS billing
  // or if their org is on a paid tier (not Starter)

  const { data: user } = useQueryOwnUser({})

  const { data: organizationTier, isFetched: saasTierIsFetched } =
    useQueryOrganizationSaasTier({ orgId: user?.organizationId })

  const hasSaasFeatures =
    (!organizationTier && saasTierIsFetched) || // excluded from SaaS OR
    (organizationTier !== undefined && organizationTier?.tier !== 'STARTER') // not Starter tier

  return hasSaasFeatures
}

interface QueryOrganizationUsagesOptions
  extends ExtendBaseQueryOptions<
    OrganizationUsagesResponse,
    OrganizationUsagesResponse
  > {
  orgId: string | undefined
}

export const useQueryOrganizationUsages = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrganizationUsagesOptions) => {
  return useExtendQuery<OrganizationUsagesResponse>({
    queryKey: orgQueryKeys.organizationUsages(orgId),
    queryUrl: () => `saas/${orgId}/usages`,
    enabled: enabled && !!orgId,
    ...rest,
  })
}

interface QueryOrganizationSubscriptionOptions
  extends ExtendBaseQueryOptions<SubscriptionResponse, SubscriptionResponse> {
  orgId: string | undefined
}

export const useQueryOrganizationSubscription = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrganizationSubscriptionOptions) => {
  return useExtendQuery<SubscriptionResponse, SubscriptionResponse>({
    queryKey: orgQueryKeys.organizationSubscription(orgId),
    queryUrl: () => `saas/${orgId}/subscription`,
    select: (data) => {
      return data
    },
    enabled: enabled && !!orgId,
    ...rest,
  })
}

interface PostCreateSubscriptionRequest
  extends UseExtendMutationVariablesBody<{
    billingPeriod: string
    plan?: Extract<SaasTierType, 'STARTER' | 'PRO'>
    issuerId?: string
  }> {
  orgId: string
  redirect: boolean
}

export const usePostCreateSubscription = () =>
  useExtendMutation<SaasUrlResponse, PostCreateSubscriptionRequest>(
    MutationMethod.POST,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/createsubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutManageSubscriptionRequest
  extends UseExtendMutationVariablesBody<{}> {
  orgId: string
  redirect: boolean
}

export const usePutManageSubscription = () =>
  useExtendMutation<SaasUrlResponse, PutManageSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/managesubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutReactivateSubscriptionRequest
  extends UseExtendMutationVariablesBody<{ billingPeriod: string }> {
  orgId: string
  redirect: boolean
}

export const usePutReactivateSubscription = () =>
  useExtendMutation<SaasUrlResponse, PutReactivateSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/reactivatesubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutRenewSubscriptionRequest
  extends UseExtendMutationVariablesBody<{}> {
  orgId: string
}

export const usePutRenewSubscriptionRequest = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<boolean, PutRenewSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/uncancelsubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.organizationSubscription(variables.orgId),
        })
      },
    },
  )
}

interface PostEnterpriseRequest
  extends UseExtendMutationVariablesBody<{
    intention: string
    issuerId?: string
  }> {
  orgId: string
}

export const usePostEnterpriseRequest = () => {
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<{ response: string }, PostEnterpriseRequest>(
    MutationMethod.POST,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/enterpriserequest`,
      onSuccess: () => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasEnterpriseRequestSuccessToastMessage />,
          }),
        )
      },
      onError: () => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasEnterpriseRequestFailureToastMessage />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      },
    },
  )
}

interface CancelSubscriptionRequest extends UseExtendMutationVariablesBody {
  orgId: string
  tier?: SaasTierType
}

export const useCancelSubscription = () => {
  const queryClient = useQueryClient()
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<unknown, CancelSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/cancelsubscription`,
      onSuccess: (_, { tier, orgId }) => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <SaasCancelSubscriptionSuccessToastMessage tier={tier} />
            ),
          }),
        )
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.organizationSubscription(orgId),
        })
      },
      onError: () => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasCancelSubscriptionFailureToastMessage />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      },
    },
  )
}

interface PostChurnSurveyRequest
  extends UseExtendMutationVariablesBody<ChurnSurveyRequest> {
  orgId: string
}

export const usePostChurnSurvey = () =>
  useExtendMutation<unknown, PostChurnSurveyRequest>(MutationMethod.POST, {
    mutationUrl: ({ orgId }) => `/saas/${orgId}/churnsurvey`,
    onError: () => {
      // Fire-and-forget — fail silently
    },
  })

interface QueryChurnSurveyReasonsOptions
  extends ExtendBaseQueryOptions<ChurnSurveyReasonsResponse> {
  orgId: string | undefined
}

export const useQueryChurnSurveyReasons = ({
  orgId,
  enabled = true,
  ...rest
}: QueryChurnSurveyReasonsOptions) => {
  return useExtendQuery<ChurnSurveyReasonsResponse>({
    queryKey: orgQueryKeys.churnSurveyReasons(orgId),
    queryUrl: () => `saas/${orgId}/churnsurvey/reasons`,
    enabled: enabled && !!orgId,
    ...rest,
  })
}
